import {
  ApplicationRef,
  EnvironmentProviders,
  inject,
  makeEnvironmentProviders,
  provideAppInitializer,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { SwRegistrationOptions, provideServiceWorker } from '@angular/service-worker';
import { Observable, filter, from, race, switchMap, take, timer } from 'rxjs';
import { environment } from '../../../environments/environment';
import { MrezaService } from './mreza.service';
import { PwaAzuriranjeService } from './pwa-azuriranje.service';
import { PwaInstalacijaService } from './pwa-instalacija.service';
import { PwaManifestService } from './pwa-manifest.service';
import { jePwaRuta } from './pwa-rute';

/** Najdulje čekanje da se aplikacija smiri prije registracije SW-a (dashboard može stalno nešto dohvaćati). */
const CEKANJE_STABILNOSTI_MS = 5000;

/**
 * PWA osnova: service worker (ngsw), manifest samo na rutama aplikacije, instalacija, mreža.
 *
 * Service worker se registrira tek kad korisnik dođe na rutu aplikacije (`/dashboard`), ne na
 * javnim stranicama: hladni promet s oglasa ne preuzima cijelu aplikaciju u pozadini.
 * Jednom registriran, SW (scope `/`) služi i javne stranice tog preglednika iz cachea.
 */
export function providePwa(): EnvironmentProviders {
  return makeEnvironmentProviders([
    provideServiceWorker('ngsw-worker.js'),
    // Zadnji provider pobjeđuje: opcije s registracijom vezanom za rutu.
    { provide: SwRegistrationOptions, useFactory: pwaOpcijeRegistracije },
    provideAppInitializer(() => {
      inject(PwaManifestService).pokreni();
      inject(PwaInstalacijaService);
      inject(MrezaService);
      inject(PwaAzuriranjeService).pokreni();
    }),
  ]);
}

export function pwaOpcijeRegistracije(): SwRegistrationOptions {
  const router = inject(Router);
  const appRef = inject(ApplicationRef);
  return {
    enabled: environment.pwa,
    registrationStrategy: () => registracijaNaPwaRuti(router, appRef),
  };
}

/** Emitira jednom: prva navigacija na rutu aplikacije, pa stabilna aplikacija (ili isteklo čekanje). */
export function registracijaNaPwaRuti(
  router: Router,
  appRef: ApplicationRef,
  cekanjeMs = CEKANJE_STABILNOSTI_MS,
): Observable<unknown> {
  return router.events.pipe(
    filter((e): e is NavigationEnd => e instanceof NavigationEnd),
    filter(e => jePwaRuta(e.urlAfterRedirects)),
    take(1),
    switchMap(() => race(from(appRef.whenStable()), timer(cekanjeMs))),
    take(1),
  );
}
